import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Coffee, Heart, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { images, restaurantInfo, cafeItems } from '../data/mock';

const KahvilaPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 overflow-hidden">
        <div className="absolute top-10 right-20 w-32 h-32 border-4 border-emerald-500/20 rounded-full" />
        <div className="absolute bottom-20 left-10 w-48 h-48 border-4 border-emerald-500/10 rounded-full" />
        
        <div className="absolute inset-0 opacity-30">
          <img
            src={images.kahvilaHero}
            alt="Kahvila"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="font-handwritten text-4xl text-emerald-400 block mb-2">Hyvää huomenta!</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Kahvila
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Arkiaamut alkavat meillä jo kello 7:45. 
            <span className="font-handwritten text-xl text-emerald-400"> Tuoreen leivonnaisen tuoksu ja lämmin kahvi odottavat!</span>
          </p>
        </div>
      </section>

      {/* Café Hours - Cute */}
      <section className="bg-emerald-500 text-white py-4 relative">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="flex flex-wrap justify-center items-center gap-2 text-sm md:text-base">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <Coffee className="w-5 h-5" />
              <span><strong>Kahvila avoinna:</strong> ma-pe {restaurantInfo.cafeHours.weekdays}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Storytelling Section - The Heart of the Page */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-amber-100 rounded-full opacity-50" />
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-emerald-100 rounded-full opacity-40" />
        
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <Coffee className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">
              Kun aamu alkaa <span className="font-handwritten text-emerald-600">tuoksusta</span>
            </h2>
          </div>
          
          <div className="space-y-6 text-stone-600 leading-relaxed text-lg">
            <p className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-100 transform -rotate-[0.3deg]">
              Arkiaamuisin, kun kaupunki vasta heräilee, meidän keittiössä on jo valot päällä. Uuni on lämmin, taikina nousee ja ensimmäiset pellilliset menevät sisään ennen kuin kello ehtii kahdeksaan.
            </p>
            
            <p>
              <strong className="text-amber-700">Se tuoksu.</strong> Voi, kaneli ja vastaleivottu pulla. Sellainen, joka leviää keittiöstä saliin ja joskus 
              <span className="font-handwritten text-2xl text-emerald-600"> ihan kadulle asti.</span>
            </p>

            <div className="bg-gradient-to-r from-amber-100 to-emerald-100 p-8 rounded-3xl my-10 border-l-4 border-emerald-400 transform rotate-[0.5deg] hover:rotate-0 transition-transform">
              <p className="text-stone-700 italic text-xl leading-relaxed m-0">
                Moni sanoo, että täällä käynti tuo mieleen lapsuuden aamut. Hetket, jolloin istuttiin keittiön pöydän ääressä ja 
                <span className="font-handwritten text-2xl text-amber-700 not-italic"> maailma sai vielä odottaa.</span>
              </p>
              <Heart className="w-6 h-6 text-red-400 mt-4" />
            </div>

            <p className="bg-stone-50 p-6 rounded-2xl border-2 border-stone-100 transform rotate-[0.3deg]">
              Ehkä siksi täällä viihdytään. Ehkä siksi <span className="bg-amber-200 px-2 py-1 rounded">kahvi maistuu vähän paremmalta.</span>
            </p>

            <p>
              Kahvilamme on auki arkena jo aikaisin. Tule istahtamaan hetkeksi, tapaamaan tuttuja tai vain rauhoittumaan ennen työpäivän alkua.
            </p>

            <p className="text-center text-xl">
              <span className="font-handwritten text-2xl text-emerald-600">Meillä ei ole kiire – eikä sinullakaan tarvitse olla.</span>
              <Sparkles className="w-5 h-5 text-emerald-500 inline-block ml-2" />
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer - Fun cards */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-stone-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="font-handwritten text-3xl text-emerald-500">Kahvipöydässä</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6 mt-2">
                Mitä kahvilasta löytyy?
              </h2>
              <ul className="space-y-3">
                {cafeItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-amber-100 transform hover:-rotate-[0.5deg] transition-transform">
                    <span className="w-3 h-3 bg-emerald-500 rounded-full flex-shrink-0 animate-pulse" />
                    <span className="text-stone-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-stone-600 leading-relaxed bg-amber-50 p-4 rounded-xl border-2 border-dashed border-amber-200">
                <span className="font-handwritten text-xl text-emerald-600">Psst!</span> Leivonnaisemme tehdään paikan päällä – tuoreutta arvostaen. Valikoimamme vaihtelee päivittäin!
              </p>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-amber-200 rounded-3xl -rotate-2" />
              <img
                src={images.kahvilaPastries}
                alt="Kahvilan leivonnaiset"
                className="relative rounded-2xl shadow-xl w-full border-4 border-white"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-3 animate-bounce">
                <span className="font-handwritten text-2xl text-emerald-600">Tuoretta!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Morning atmosphere - CTA */}
      <section className="py-16 md:py-24 bg-emerald-500 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <span className="font-handwritten text-4xl text-amber-100 block mb-4">Tervetuloa meille!</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Lämpöä aamuun
          </h2>
          <p className="text-amber-100 text-lg leading-relaxed mb-6 max-w-2xl mx-auto">
            Kahvin tuoksu, vastaleivottu pulla ja hetki ilman kiirettä. Kahvilassamme aamu saa alkaa rauhassa.
          </p>
          <p className="font-handwritten text-2xl text-white mb-8">
            Tule sellaisena kuin olet.
          </p>
          <Link to="/yhteystiedot">
            <Button className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-4 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all group">
              Löydä perille
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KahvilaPage;
